import { useMemo, useState } from 'react'
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { PlusIcon, ReceiptIcon, XIcon } from 'lucide-react'

import { cn } from 'cn'
import { Button } from '@ems/ui/components/button'
import { SearchInput } from '@ems/ui/patterns/search-input'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatDate, formatMoney } from '@ems/ui/lib/format'

import { OptionSelect } from '#admin/components/option-select.tsx'
import { pageSlice, TablePagination, usePagination } from '#admin/components/table-pagination.tsx'
import { ExportLink } from '#admin/features/billing/export-link.tsx'
import { useLiveSearch } from '#admin/lib/use-live-search.ts'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import {
  INVOICE_STATUSES,
  invoiceTone,
  isPastDue,
  label,
  outstanding,
  type Invoice,
} from '#admin/features/billing/api.ts'
import { InvoicesSkeleton } from '#admin/features/billing/billing-skeletons.tsx'
import { InvoiceDialog } from '#admin/features/billing/invoice-dialog.tsx'
import { invoicesQuery, invoiceSummaryQuery } from '#admin/features/billing/queries.ts'
import {
  invoiceFiltersOf,
  parseInvoiceSearch,
  type InvoiceSearch,
} from '#admin/features/billing/search.ts'
import { SummaryTiles } from '#admin/features/billing/summary-tiles.tsx'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/**
 * `/billing/invoices` — the invoice ledger: what the edition is owed, then every invoice, filtered from
 * the URL so "overdue" or "drafts" is a link somebody can send.
 */
export const Route = createFileRoute('/_authenticated/billing/invoices/')({
  pendingComponent: InvoicesSkeleton,
  validateSearch: parseInvoiceSearch,
  loaderDeps: ({ search }) => invoiceFiltersOf(search),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(invoicesQuery(deps)),
      context.queryClient.ensureQueryData(invoiceSummaryQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ]),
  component: InvoicesPage,
})

/** `all` rather than `''`: Radix reserves the empty string for "nothing chosen". */
const ANY = 'all'

function InvoicesPage() {
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const { data: summary } = useSuspenseQuery(invoiceSummaryQuery())
  const canManage = can(session, 'billing.manage')
  const search = Route.useSearch()
  const filters = invoiceFiltersOf(search)
  const navigate = useNavigate({ from: Route.fullPath })
  const [draft, setDraft] = useLiveSearch(
    filters.q,
    (term) =>
      void navigate({ search: (prev) => parseInvoiceSearch({ ...prev, q: term }), replace: true }),
  )
  const [creating, setCreating] = useState(false)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(
    invoicesQuery(filters),
  )
  const invoices = data.pages.flatMap((page) => page.items)
  const total = data.pages[0]?.total ?? 0
  // One page at a time, 10 rows unless the operator chooses more. Any filter, search or new page
  // size starts again at the first page.
  const { page, setPage, pageSize, setPageSize } = usePagination(JSON.stringify(filters))
  const paged = pageSlice(invoices, page, pageSize)
  const filtered = filters.q !== '' || filters.status !== ''

  const setFilter = (key: keyof InvoiceSearch, value: string) =>
    void navigate({ search: (prev) => parseInvoiceSearch({ ...prev, [key]: value }) })

  const columns = useMemo<ColumnDef<Invoice, never>[]>(
    () => [
      {
        accessorKey: 'number',
        header: 'Number',
        meta: { primary: true },
        cell: ({ row }) => (
          <Link
            to="/billing/invoices/$invoiceId"
            params={{ invoiceId: row.original.id }}
            // Stretched over the row, so the whole row opens the invoice.
            className="font-mono text-xs after:absolute after:inset-0 hover:underline"
          >
            {row.original.number ?? 'draft'}
          </Link>
        ),
      },
      {
        id: 'billTo',
        header: 'Billed to',
        meta: { truncate: true },
        cell: ({ row }) => (
          <span className="flex min-w-0 flex-col">
            <span className="truncate">{row.original.billToName}</span>
            {row.original.billToEmail === null ? null : (
              <span className="text-muted-foreground truncate text-xs">
                {row.original.billToEmail}
              </span>
            )}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <StatusBadge tone={invoiceTone(row.original.status)}>
              {label(row.original.status)}
            </StatusBadge>
            {/* Every finance mark, whatever the ledger says. `unpaid` is the column's default, so it
                gets no badge: it would sit beside every gateway-paid invoice. */}
            {row.original.companyInvoiceStatus === 'paid' ? (
              <StatusBadge tone="info">Company paid</StatusBadge>
            ) : null}
          </span>
        ),
      },
      {
        id: 'due',
        header: 'Due',
        cell: ({ row }) => {
          const { dueAt } = row.original
          if (dueAt === null) return <span className="text-muted-foreground">—</span>
          const late = isPastDue(row.original)
          return (
            <span className={cn(late ? 'text-destructive font-medium' : 'text-muted-foreground')}>
              {formatDate(dueAt)}
              {late ? <span className="sr-only"> (past due)</span> : null}
            </span>
          )
        },
      },
      {
        id: 'total',
        header: 'Total',
        meta: { align: 'end' },
        cell: ({ row }) => formatMoney(row.original.total, row.original.currency),
      },
      {
        id: 'outstanding',
        header: 'Outstanding',
        meta: { align: 'end' },
        cell: ({ row }) => {
          const owed = outstanding(row.original)
          return (
            <span className={owed === 0 ? 'text-muted-foreground' : 'font-medium'}>
              {row.original.status === 'voided' ? '—' : formatMoney(owed, row.original.currency)}
            </span>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Invoices"
        description="Raised for registrations and stand bookings, or by hand. Figures are per currency."
        actions={
          <>
            <ExportLink
              path="/v1/billing/admin/invoices/export"
              filters={filters}
              filtered={filtered}
            />
            {canManage ? (
              <Button onClick={() => setCreating(true)}>
                <PlusIcon />
                New invoice
              </Button>
            ) : null}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="billing" />}

      <SummaryTiles summary={summary} />

      <div className="flex flex-wrap items-end gap-2">
        <div role="search" className="flex w-full min-w-0 sm:w-auto sm:min-w-64 sm:flex-1">
          <SearchInput
            aria-label="Search by number, name or email"
            placeholder="Search number, name or email"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
        </div>
        <OptionSelect
          aria-label="Status"
          className="w-full sm:w-44"
          value={filters.status === '' ? ANY : filters.status}
          onValueChange={(value) => setFilter('status', value)}
          options={[
            { value: ANY, label: 'Any status' },
            ...INVOICE_STATUSES.map((value) => ({
              value,
              label: `${label(value)} · ${summary.counts[value]}`,
            })),
          ]}
        />
        {/* Beside the filters it clears, and only while one is on. */}
        {filtered ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setDraft('')
              void navigate({ search: {} })
            }}
            className="text-muted-foreground hover:text-foreground h-9 gap-1.5"
          >
            <XIcon className="size-4" />
            Clear filters
          </Button>
        ) : null}
      </div>

      <DataTable
        caption="Invoices"
        title={`${filtered ? 'Matching invoices' : 'All invoices'} (${total})`}
        // The total is the server's count of every match. The rows come a batch at a time, so
        // Next asks for the next batch when its page goes past the rows that are here. Until they
        // arrive, the table stays on the last page it can show. Only with rows: an empty table
        // shows its empty state, and no line under it.
        footer={
          total === 0 ? undefined : (
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={total}
              onPageChange={(nextPage) => {
                setPage(nextPage)
                // Not while a fetch is on its way: a second click would ask for the same rows.
                if (
                  (nextPage + 1) * pageSize > invoices.length &&
                  hasNextPage &&
                  !isFetchingNextPage
                ) {
                  void fetchNextPage()
                }
              }}
              ariaLabel="Pages of invoices"
            />
          )
        }
        columns={columns}
        data={paged.rows}
        getRowId={(invoice) => invoice.id}
        empty={
          <EmptyState
            icon={<ReceiptIcon />}
            title={filtered ? 'Nothing matches' : 'No invoices yet'}
            description={
              filtered
                ? 'The search looks at invoice numbers, names and emails.'
                : 'An invoice is raised when a registration or a stand booking is confirmed.'
            }
          />
        }
      />

      {creating ? (
        <InvoiceDialog
          invoice={null}
          currency={manifest?.tenant.currencyCode ?? 'IDR'}
          zone={manifest?.tenant.timezone ?? 'UTC'}
          onClose={() => setCreating(false)}
          onSaved={(invoice) =>
            void navigate({ to: '/billing/invoices/$invoiceId', params: { invoiceId: invoice.id } })
          }
        />
      ) : null}
    </div>
  )
}
